/* eslint-disable react/prop-types */
/* eslint-disable react/display-name */
import React from 'react'
import { graphql } from 'gatsby'
import T from 'prop-types'
import BlockContent from '@sanity/block-content-to-react'

import { Article } from '@components'
import SEO from '../components/SEO'
import Section from '../components/Section'
import Recipes from '../components/RecipeList'
import Propaganda from '../components/Propaganda'

const BlockRenderer = type => props => {
  const style = props.node.style || 'normal'

  if (/^h\d/.test(style)) {
    const level = `h${style.replace(/[^\d]/g, '')}`
    return <Article.SubTitle type={level}>{props.children}</Article.SubTitle>
  }

  return <Article.Text type={type}>{props.children}</Article.Text>
}

const serializers = type => ({
  list: props => <Article.List type="tight">{props.children}</Article.List>,
  listItem: props => <Article.ListItem>{props.children}</Article.ListItem>,
  types: {
    block: BlockRenderer(type),
    textAndImages: props => (
      <Article.ImageSection
        images={props.node.images}
        type={props.node.imageWidth}
      >
        <BlockContent blocks={props.node.content} serializers={serializers()} />
      </Article.ImageSection>
    ),
    textAndImage: props => (
      <Article.ImageSection
        images={[props.node.image]}
        type={props.node.imageWidth}
      >
        <BlockContent blocks={props.node.content} serializers={serializers()} />
      </Article.ImageSection>
    ),
    quote: props => {
      console.log(props)
      return (
        <Article.Quote source={props.node.source}>
          {props.node.quote}
        </Article.Quote>
      )
    },
    images: props => <Article.Images images={props.node.images} />,
  },
})

export default function Page({
  data: {
    foodsteps: { article, products },
  },
}) {
  return (
    <>
      <SEO
        title={article.node.title}
        description={article.node.preamble}
        image={article?.node?.image?.ogImageUrl}
      />

      <Section type="article" node="div">
        <Article
          title={article.node.title}
          preamble={article.node.preamble}
          image={article.node?.image?.url}
        >
          <BlockContent
            blocks={article.node.content}
            serializers={serializers('tight')}
          />

          {article.node.ctaTitle && article.node.ctaText && (
            <Propaganda
              products={products}
              title={article.node.ctaTitle}
              preamble={article.node.ctaText}
              fluid
            />
          )}

          {article.node.recipes.edges.length > 0 && (
            <Recipes recipes={article.node.recipes.edges} />
          )}
        </Article>
      </Section>
    </>
  )
}

Page.propTypes = {
  data: T.shape({
    foodsteps: T.shape({
      article: T.object.isRequired,
      products: T.array.isRequired,
    }).isRequired,
  }).isRequired,
}

export const query = graphql`
  query ArticleTemplateQuery($slug: String!) {
    foodsteps {
      products {
        id
        title
        description
        includes
        variants {
          promotion {
            price
            title
            id
            description
            eligible
          }
          price
          months
        }
      }

      article(slug: $slug) {
        node {
          id
          slug
          title
          preamble
          ctaTitle
          ctaText
          image {
            url
            ogImageUrl: url(width: 1200, height: 630)
          }
          content {
            _key
            _type
            ...PortableContent
          }
          recipes {
            edges {
              node {
                id
                slug
                title
                image {
                  url(width: 391, height: 521)
                }
              }
            }
          }
        }
      }
    }
  }

  fragment PortableContent on FS_PortableContent {
    _key
    _type
    ...PortableContentBlock
    ...PortableContentQuote
    ...PortableContentImages
    ...PortableContentTextAndImage
    ...PortableContentTextAndImages
  }

  fragment PortableContentBlock on FS_PortableContentBlock {
    _key
    _type
    text
    style
    level
    listItem
    marks
    markDefs {
      _type
      _key
      ... on FS_PortableLinkMarkDefinition {
        href
      }
    }
    children {
      ... on FS_PortableContentBlock {
        _key
        _type
        text
        style
        level
        listItem
        marks
        markDefs {
          _type
          _key
          ... on FS_PortableLinkMarkDefinition {
            href
          }
        }
        children {
          ... on FS_PortableContentBlock {
            _key
            _type
            text
            style
            level
            listItem
            marks
            markDefs {
              _type
              _key
              ... on FS_PortableLinkMarkDefinition {
                href
              }
            }
          }
        }
      }
    }
  }

  fragment PortableContentQuote on FS_PortableContentQuote {
    quote
    source
  }

  fragment PortableContentImages on FS_PortableContentImages {
    images {
      url
      alt
    }
  }

  fragment PortableContentTextAndImage on FS_PortableContentTextAndImage {
    imageWidth
    image {
      url
      alt
    }
    content {
      ...PortableContentBlock
    }
  }

  fragment PortableContentTextAndImages on FS_PortableContentTextAndImages {
    images {
      url
      alt
    }
    content {
      ...PortableContentBlock
    }
  }
`
